import { Component, OnInit, OnDestroy } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { Subject, Observable } from 'rxjs';
import * as _ from 'underscore';
import { RootStoreState } from 'src/app/root-store';
import { takeUntil } from 'rxjs/operators';
import { ColumnConfigItem } from 'src/app/models/ColumnConfigItem';

import { getCurrentUser } from 'src/app/root-store/main-app-store/selectors/auth.selector';
import { Transport } from 'src/app/models/Transport.model';
import { getTransportAreas, getTransportAreasError, getTransportAreasLoading, getTransportLoads, getTransportLoadsError, getTransportLoadsLoading, getTransportOptions, getTransportOptionsError, getTransportOptionsLoading, getTransportPrice, getTransportPriceError, getTransportPriceLoading } from 'src/app/root-store/kwikspace-app-store/selectors/transport.selector';
import { ClearTransportPriceList, GetTransportAreas, GetTransportLoads, GetTransportOptions, GetTransportPrice } from 'src/app/root-store/kwikspace-app-store/actions/transport.action';
import { PaginationService } from 'src/app/services/pagination.service';

@Component({
  selector: 'kwikspace-transport',
  templateUrl: './transport.component.html',
  styleUrls: ['./transport.component.scss']
})
export class TransportComponent implements OnInit, OnDestroy {
  private ngUnsubscribe = new Subject();

  transportChargesLoading$: Observable<any> = this.store.select(getTransportPriceLoading);
  transportChargesError$: Observable<any> = this.store.select(getTransportPriceError);

  transportOptionsLoading$: Observable<any> = this.store.select(getTransportOptionsLoading);
  transportOptionsError$: Observable<any> = this.store.select(getTransportOptionsError);

  transportSizesLoading$: Observable<any> = this.store.select(getTransportLoadsLoading);
  transportSizesError$: Observable<any> = this.store.select(getTransportLoadsError);

  transportAreasLoading$: Observable<any> = this.store.select(getTransportAreasLoading);
  transportAreasError$: Observable<any> = this.store.select(getTransportAreasError);

  transportCharges: Transport[];
  pageCount = 1;
  pageLinks;
  itemCount = 0;
  currentPage = 1;
  pageToLoad;

  iconClass: string;
  columnConfig: ColumnConfigItem[];
  filterDropdownOptions: any[];

  filterBy: any;
  sortBy: any;

  transportSiting: any[] = [];
  transportLocations: any[] = [];
  transportSizes: any[] = [];

  hasTransport: boolean = false;

  constructor(
    private store: Store<RootStoreState.RootState>,
    private paginationService: PaginationService
  ) { }

  ngOnInit() {
    this.store.dispatch(new GetTransportOptions());
    this.store.dispatch(new GetTransportLoads());
    this.store.dispatch(new GetTransportAreas({}));

    this.store
      .select(getCurrentUser)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((res) => {
        if (res) {
          this.getTransportCharges();
        }
      });

    this.store
      .select(getTransportPrice)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((transportCharges: any) => {
        if (transportCharges) {
          let _transportCharges = transportCharges.results ? transportCharges.results : transportCharges.length > 0 ? transportCharges : [];

          this.itemCount = transportCharges.count;
          this.pageLinks = transportCharges.links;
          this.pageCount = this.itemCount < 10 ? 1 : Math.ceil(this.itemCount / 10);

          if (_transportCharges.length > 0) {
            this.filterBy = '';
            this.sortBy = '';
          }

          this.buildTransportTable(_transportCharges);
        }
      });

    this.store
      .select(getTransportOptions)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((options: any) => {
        if (options) {
          this.transportSiting = options;
        }
      });

    this.store
      .select(getTransportLoads)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((loads: any) => {
        if (loads) {
          loads.forEach(load => {
            Object.keys(load).forEach(key => {
              if (key == 'size') {
                load.name = load[key];
              }
            })
          });
          this.transportSizes = loads;
        }
      });

    this.store
      .select(getTransportAreas)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((areas: any) => {
        if (areas) {
          areas.forEach(area => {
            Object.keys(area).forEach(key => {
              if (key == 'name') {
                area[key] = area.district.province.name + ' - ' + area.district.name + ' - ' + area.name;
              }
            })
          });
          this.transportLocations = areas;
        }
      });

    this.iconClass = 'fa fa-user text-white';

    this.columnConfig = [
      {
        oldName: 'id',
        isSortable: false,
        isVisible: false
      }
    ];
  }

  buildTransportTable(transports) {
    if (transports.length > 0) {
      transports.forEach(item => {
        Object.keys(item).forEach(key => {
          let obj = item[key];

          if (key == 'transportLocation') {
            item[key] = obj.district.province.name + ' - ' + obj.district.name + ' - ' + obj.name;
          }
          if (key == 'loadSize') {
            item[key] = obj.size;
          }
          if (key == 'transportCharge') {
            item[key] = obj.name;
          }
          if (key == 'priceType') {
            if (item[key] != null) {
              item[key] = obj.name;
            }
            else {
              item[key] = 'none'
            }
          }
        });
      });
    }

    this.getFilterOptions();

    this.transportCharges = transports;
    this.hasTransport = this.transportCharges ? true : false;
  }

  getFilterOptions() {
    this.filterDropdownOptions = [
      {
        id: 'clear',
        key: 'Clear',
        filters: [
          {
            id: 'clear',
            name: 'Clear Filters'
          }
        ]
      },
      {
        id: 'transport_charge',
        key: 'Siting',
        filters: this.transportSiting
      },
      {
        id: 'transport_location',
        key: 'Location',
        filters: this.transportLocations
      },
      {
        id: 'load_size',
        key: 'Size',
        filters: this.transportSizes
      },
    ]
  }

  getTransportCharges() {
    this._reloadList();
  }

  retry() {
    this.getTransportCharges();
  }

  sort(sortableOptions: any) {
    this.currentPage = 1;

    this.sortBy = sortableOptions;
    this._reloadList();
  }

  filter(filterBy: object) {
    this.currentPage = 1;
    this.sortBy = '';

    this.filterBy = filterBy;
    this._reloadList();
  }

  paginate(page: string) {
    let pageProps: any = this.paginationService.getPageProps(page, this.pageCount, this.pageLinks);

    this.currentPage = pageProps.current;
    this.pageToLoad = pageProps.pageToLoad;

    this._reloadList();
  }

  private _reloadList() {
    this.store.dispatch(new ClearTransportPriceList());
    this._loadList();
  }

  _loadList() {
    this.store.dispatch(
      new GetTransportPrice({
        filter: this.filterBy,
        sort: this.sortBy,
        page: this.pageToLoad,
      })
    );
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

}
